import { useEffect, useState } from "react";
import { FileDown, Printer } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { formatDateBr } from "@/lib/gente-gestao/format";
import { downloadGgFile } from "@/services/genteGestao";
import {
  ESCUTA_STATUS_OPCOES,
  ESCUTA_TIPO_OPCOES,
  atualizarEscuta,
  extractEscutaError,
  fetchEscutaDetalhe,
  fetchEscutaLista,
  pdfCartazUrl,
  pdfOpiniaoUrl,
  type EscutaDetalhe,
  type EscutaListaItem,
  type EscutaResumo,
} from "@/services/escuta";

const RESUMO_VAZIO: EscutaResumo = {
  abertas: 0,
  recebidas: 0,
  emAnalise: 0,
  aguardando: 0,
  concluidasMes: 0,
  anonimasMes: 0,
};

export function GenteGestaoEscutaPage() {
  const [items, setItems] = useState<EscutaListaItem[]>([]);
  const [resumo, setResumo] = useState<EscutaResumo>(RESUMO_VAZIO);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const [status, setStatus] = useState("");
  const [anonima, setAnonima] = useState("");
  const [detalhe, setDetalhe] = useState<EscutaDetalhe | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [baixando, setBaixando] = useState<"cartaz" | "opiniao" | null>(null);

  async function load() {
    setLoading(true);
    setErro(null);
    try {
      const res = await fetchEscutaLista({
        q: q.trim() || undefined,
        tipo: tipo || undefined,
        status: status || undefined,
        anonima: anonima || undefined,
      });
      setItems(res.manifestacoes);
      setResumo(res.resumo);
    } catch (e) {
      setErro(extractEscutaError(e, "Não foi possível carregar o canal."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function abrir(id: string) {
    setErro(null);
    try {
      setDetalhe(await fetchEscutaDetalhe(id));
    } catch (e) {
      setErro(extractEscutaError(e, "Não foi possível abrir a manifestação."));
    }
  }

  async function salvar() {
    if (!detalhe) return;
    setSalvando(true);
    setErro(null);
    try {
      const atualizado = await atualizarEscuta(detalhe.id, {
        status: detalhe.status,
        responsavel: detalhe.responsavel ?? "",
        notasInternas: detalhe.notasInternas ?? "",
        resposta: detalhe.resposta ?? "",
      });
      setDetalhe(atualizado);
      await load();
    } catch (e) {
      setErro(extractEscutaError(e, "Não foi possível salvar."));
    } finally {
      setSalvando(false);
    }
  }

  async function baixar(kind: "cartaz" | "opiniao") {
    setBaixando(kind);
    setErro(null);
    try {
      if (kind === "cartaz") {
        await downloadGgFile(pdfCartazUrl(), "canal-escuta-ativa-semog.pdf", "application/pdf");
      } else if (detalhe) {
        await downloadGgFile(pdfOpiniaoUrl(detalhe.id), `escuta-${detalhe.protocolo}.pdf`, "application/pdf");
      }
    } catch (e) {
      setErro(extractEscutaError(e, "Não foi possível gerar o PDF."));
    } finally {
      setBaixando(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <h2 className="text-base font-bold text-slate-800">Canal de Escuta Ativa SEMOG</h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Espaço seguro e confidencial para opiniões, sugestões, reclamações, dúvidas, elogios, denúncias, assédio,
          conflitos, sobrecarga e riscos à saúde e à segurança. A identificação é escolha de quem registra. As
          manifestações são tratadas com sigilo e sem retaliação.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button leftIcon={<Printer className="h-4 w-4" />} loading={baixando === "cartaz"} onClick={() => void baixar("cartaz")}>
            PDF do cartaz com QR Code
          </Button>
          <a
            href="/escuta"
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 items-center rounded-lg bg-slate-100 px-4 text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Abrir formulário
          </a>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Card title="Abertas" value={String(resumo.abertas)} />
        <Card title="Recebidas" value={String(resumo.recebidas)} />
        <Card title="Em análise ou apuração" value={String(resumo.emAnalise)} />
        <Card title="Aguardando retorno" value={String(resumo.aguardando)} />
        <Card title="Concluídas no mês" value={String(resumo.concluidasMes)} />
        <Card title="Anônimas no mês" value={String(resumo.anonimasMes)} />
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Input label="Busca" value={q} placeholder="Protocolo ou assunto" onChange={(e) => setQ(e.target.value)} />
        <Select
          label="Tipo"
          value={tipo}
          placeholder="Todos"
          options={ESCUTA_TIPO_OPCOES.map((o) => ({ value: o.value, label: o.label }))}
          onChange={(e) => setTipo(e.target.value)}
        />
        <Select
          label="Status"
          value={status}
          placeholder="Todos"
          options={ESCUTA_STATUS_OPCOES.map((o) => ({ value: o.value, label: o.label }))}
          onChange={(e) => setStatus(e.target.value)}
        />
        <Select
          label="Identificação"
          value={anonima}
          placeholder="Todas"
          options={[
            { value: "1", label: "Somente anônimas" },
            { value: "0", label: "Somente identificadas" },
          ]}
          onChange={(e) => setAnonima(e.target.value)}
        />
      </div>
      <div>
        <Button variant="secondary" onClick={() => void load()}>
          Filtrar
        </Button>
      </div>

      {erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-card">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader label="Carregando manifestações..." />
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-slate-500">Nenhuma manifestação encontrada.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Protocolo</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Assunto</th>
                <th className="px-4 py-3">Identificação</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                  onClick={() => void abrir(item.id)}
                >
                  <td className="px-4 py-3 font-medium">{item.protocolo}</td>
                  <td className="px-4 py-3">{formatDateBr(item.createdAt)}</td>
                  <td className="px-4 py-3">
                    {item.tipoLabel}
                    {item.sensivel ? <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Sensível</span> : null}
                  </td>
                  <td className="px-4 py-3">{item.assunto}</td>
                  <td className="px-4 py-3">{item.anonima ? "Anônima" : item.nome}</td>
                  <td className="px-4 py-3">{item.statusLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={detalhe != null}
        onClose={() => setDetalhe(null)}
        title={detalhe ? detalhe.protocolo : "Manifestação"}
        size="lg"
        closeOnEscape
        closeOnBackdropClick
        footer={
          detalhe ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                leftIcon={<FileDown className="h-4 w-4" />}
                loading={baixando === "opiniao"}
                onClick={() => void baixar("opiniao")}
              >
                PDF da opinião
              </Button>
              <Button loading={salvando} onClick={() => void salvar()}>
                Salvar tratamento
              </Button>
            </div>
          ) : null
        }
      >
        {detalhe && (
          <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-1">
            <p className="text-sm text-slate-500">
              {detalhe.tipoLabel} · {formatDateBr(detalhe.createdAt)}
              {detalhe.sensivel ? " · Sensível" : ""}
              {detalhe.querRetorno ? " · Pediu retorno" : ""}
            </p>
            <h3 className="text-base font-bold">{detalhe.assunto}</h3>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{detalhe.relato}</p>
            <p className="text-sm text-slate-600">Onde: {detalhe.local || "—"} · Quando: {formatDateBr(detalhe.quando)}</p>
            <p className="text-sm text-slate-600">Envolve: {detalhe.envolve || "—"}</p>
            <p className="text-sm font-medium text-slate-700">
              {detalhe.anonima
                ? "Identificação: anônima. Nome, setor e contato não foram gravados."
                : `Identificação: ${detalhe.nome} · ${detalhe.setor || "sem setor"} · ${detalhe.contato || "sem contato"}`}
            </p>
            <Select
              label="Status"
              value={detalhe.status}
              options={ESCUTA_STATUS_OPCOES.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(e) => setDetalhe({ ...detalhe, status: e.target.value })}
            />
            <Input
              label="Responsável interno"
              value={detalhe.responsavel ?? ""}
              onChange={(e) => setDetalhe({ ...detalhe, responsavel: e.target.value })}
            />
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notas internas</span>
              <textarea
                className="min-h-24 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                value={detalhe.notasInternas ?? ""}
                onChange={(e) => setDetalhe({ ...detalhe, notasInternas: e.target.value })}
              />
              <span className="text-xs text-slate-500">Visível só para quem trata o canal. Não entra no PDF nem no protocolo.</span>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resposta ao colaborador</span>
              <textarea
                className="min-h-24 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                value={detalhe.resposta ?? ""}
                onChange={(e) => setDetalhe({ ...detalhe, resposta: e.target.value })}
              />
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
}
